/**
 * The contract between the generic screen host (`app-ui-screen`) and a feature.
 *
 * The host owns everything a configured screen needs that is not feature behaviour: loading
 * the screen, laying it out, the tab strip, the field popovers, validation display, the
 * footer buttons, the submit gate. A feature supplies one object — its controller — that
 * owns the record's state and the behaviour the configuration names but cannot define:
 * which figures exist, what `addRow` on a dataset means, what to seed.
 *
 * Nothing in this file names a feature. See docs/features/order-capture.md.
 */

import { AppUiAttributeVocabulary, AppUiFieldModel } from './app-ui-merge';
import {
  AppUiFieldValue,
  AppUiFieldValues,
  AppUiRecordDoc,
  AppUiRecordSnapshot,
  AppUiUploadMode,
  AppUiUploadRequest,
} from '@omni/interfaces/app-ui/record.interface';
import {
  OverlayNode,
  OverlayScreen,
} from '@omni/interfaces/app-ui/overlay.interface';
import { AppUiActionRegistry } from './app-ui-screen-actions';
import { AppUiFormLayout } from './app-ui-form';
import { AppUiRecordScopeInput } from './app-ui-expression';

/** What a controller may ask of its host. Handed over in `attach`. */
export interface AppUiScreenHost {
  /** View state changed; rebuild the render context and repaint. */
  requestRender(): void;
  /** Record state changed: repaint, and persist so the change survives the app. */
  recordChanged(): void;
  /** Dismiss the screen. */
  close(): void;
  /** The record as the device holds it now; null before its first save. */
  stored(): Promise<AppUiRecordDoc | null>;
  /** True once the record is saved, on the device or beyond, so there is something to delete. */
  isStored?(): boolean;
  /** Removes the record from the device and closes the screen. */
  discard(): Promise<void>;
  /** Show the tab with this node id. */
  selectTab(tabId: string): void;
  /** Submitted and not answered yet; the record still holds what it had before. */
  submitPending?(): boolean;
  /** The shared date picker. Resolves `yyyy-MM-dd`, or null when the rep cancelled. */
  pickDate(
    current: string | null,
    options?: AppUiDatePickOptions,
  ): Promise<string | null>;
  /** The screen's current fields, in render order. */
  fields(): AppUiFieldModel[];
  /** Asks the rep to confirm; resolves true only on the confirm button. */
  confirm(confirmation: AppUiConfirmation): Promise<boolean>;
  /** Asks the rep to pick one of `choices`; resolves the chosen id, or null when they cancel. */
  choose(
    question: Omit<AppUiConfirmation, 'confirmLabel'>,
    choices: { id: string; label: string }[],
  ): Promise<string | null>;
  /** A passing message, e.g. what an action could not do. */
  notify(message: string, tone?: 'info' | 'danger'): void;
  /**
   * Shows the app's loader while `work` runs, for a few seconds at most. `done: false` means it ran
   * longer: the loader is gone, `work` carries on, and its answer arrives through it.
   */
  waitBriefly<T>(work: Promise<T>): Promise<AppUiBriefWait<T>>;
}

/** As the app's confirmations read: the action as the title, what it means, and the button that does it. */
export interface AppUiConfirmation {
  title: string;
  message: string;
  /** The action's own verb ("Delete"); Confirm when absent. */
  confirmLabel?: string | null;
}

export interface AppUiDatePickOptions {
  /** Shown before the month heading. */
  title?: string;
  /** `yyyy-MM-dd`: the first day the rep can pick. */
  earliest?: string | null;
}

export type AppUiBriefWait<T> = { done: true; value: T } | { done: false };

/** What the host knows once the screen is loaded, offered to the controller in `init`. */
export interface AppUiScreenContext {
  screen: OverlayScreen;
  layout: AppUiFormLayout;
  fields: AppUiFieldModel[];
  isFallback: boolean;
  /** The record the rep is reopening, when there is one. */
  snapshot: AppUiRecordSnapshot | null;
  /** What the server calls that record, e.g. its order number, once a pull has seen it. */
  serverName?: string | null;
}

/**
 * What a device row (`offlineRows`) tells a dataset list that merges it with the server's rows: the
 * record's `statecode`, read against the dataset's own condition on it, and true while the device
 * holds changes the server lacks.
 */
export const DEVICE_ROW_STATE = '@statecode';
export const DEVICE_ROW_UNSENT = '@unsent';
/** On a feature list's row: the record on screen, which the list marks as current. */
export const CURRENT_ROW = '@current';

/** A list the feature fills itself (`listSource`), from records the device keeps. */
export interface AppUiListSource {
  rows(): Promise<Record<string, unknown>[]>;
  /** Online, brings those records up to date with the server; the rows are read again after it. */
  refresh?(): Promise<void>;
}

export interface AppUiScreenController {
  /** Which screen to load, and what to show when nothing is cached. */
  readonly screenKey: string;
  readonly entity?: string;
  readonly fallback: OverlayScreen;
  /** What this feature knows about attributes no configuration can say. */
  readonly vocabulary?: Record<string, AppUiAttributeVocabulary>;
  /** No record exists yet, so server-owned fields such as status have nothing to say. */
  readonly isCreate: boolean;
  /** The executors for the actions the configuration names. */
  readonly actions: AppUiActionRegistry;

  attach(host: AppUiScreenHost): void;
  /** Called once the screen is known. May load state; the host shows a loading state until it resolves. */
  init(context: AppUiScreenContext): Promise<void> | void;

  /** True while the record may be edited. */
  isEditable(): boolean;
  /** True while the feature is still loading what it needs to render. */
  isLoading(): boolean;
  /** True once the feature can render its content. False disables the form. */
  isReady(): boolean;
  /** Fetches again what the feature could not render without, at the rep's request. Online only. */
  retry?(): Promise<void>;

  values(): AppUiFieldValues;
  /** Writes a field. Returns true when the value changed. */
  setValue(field: AppUiFieldModel, value: AppUiFieldValue | null): boolean;
  /** Fills fields whose value the device already knows, e.g. the current account. */
  seedDefaults(fields: AppUiFieldModel[]): void;
  /**
   * Optional: offline, the rows a server-backed list shows from what the device holds, e.g. the
   * records saved here, newest first. Null leaves the list waiting for a connection.
   */
  offlineRows?(node: OverlayNode): Promise<Record<string, unknown>[] | null>;
  /**
   * Optional: a list node the feature fills itself, online and offline, in place of its query,
   * e.g. the rep's drafts. Null for any other list.
   */
  listSource?(node: OverlayNode): AppUiListSource | null;
  /** Optional: online, the feature's own limit on a list's query, e.g. no cancelled orders. */
  narrowQuery?(entity: string, fetchXml: string): string;
  /** Optional: the feature's last word on the rows a list's query returned, e.g. money in its own format. */
  adaptRows?(
    node: OverlayNode,
    rows: Record<string, unknown>[],
  ): Record<string, unknown>[];
  /** True when a new record holds nothing worth keeping, so leaving without saving removes it. */
  isDiscardable?(): boolean;

  /** Everything an expression may read beyond the field values. */
  scopeInput(): Omit<AppUiRecordScopeInput, 'values' | 'formatCurrency'>;
  formatCurrency(value: number): string;

  /**
   * Feature-specific bindings for nodes the layout vocabulary cannot draw, keyed by node id
   * first and node type second. The host never looks inside; the node's own component does.
   */
  extensions(): Record<string, unknown>;

  /**
   * Optional: what autosave keeps. Null while there is nothing worth keeping, e.g. before
   * the feature could load what it needs. A controller without it is never persisted.
   */
  snapshot?(): AppUiRecordSnapshot | null;
  /**
   * Optional: the action and parameters that send this record up, built now with the figures
   * the device computed. With it, Save and Submit queue the record for the upload cycle — the
   * host never learns what the record looks like on the wire. Null while nothing can be sent.
   */
  prepareUpload?(
    mode: AppUiUploadMode,
    stored?: AppUiRecordDoc | null,
  ): AppUiUploadRequest | null;
  /**
   * Optional: the feature's own reason it cannot be submitted now, in the rep's words, checked after
   * the screen's configured guards; null when it can go.
   */
  submitBlocker?(): string | null;
  /** Optional: how a record the server holds is named to the rep, e.g. "Order ORD-01234". */
  recordLabel?(record: AppUiRecordDoc): Promise<string | null>;
  /**
   * Optional: what the server calls a record it now holds (e.g. its order number), shown on screen
   * at once and returned for the device's copy. Online; null when it cannot say.
   */
  nameRecord?(record: AppUiRecordDoc): Promise<string | null>;
  /**
   * Optional: the feature's own words for a record saved, submitted or scrapped (`reason` null) or
   * refused, in place of the generic ones.
   */
  announce?(
    mode: AppUiUploadMode,
    record: AppUiRecordDoc,
    reason: string | null,
  ): Promise<string>;
  /**
   * Optional: once a submit lands and the screen stays (`afterSave: 'stay'`), take on what the
   * server now says about the record (its status, its number) so the screen shows it. Online.
   */
  showSubmitted?(record: AppUiRecordDoc): Promise<void>;
  /** Optional: the screen's title when the configuration binds none. */
  title?(): string;
  /** Optional: what the rep calls the record, e.g. its number, among screens open on top of each other. */
  recordName?(): string | null;
  /** Optional: the server record shown when there is no device record, e.g. a read-only view. */
  serverId?(): string | null;
  /** The feature's last word on the loaded screen, e.g. its own wording for a button. */
  adaptScreen?(screen: OverlayScreen): OverlayScreen;
}

/** A record's sync status as the rep reads it. */
export const SYNC_STATUS_KEYS: Record<string, string> = {
  draft: 'APP_UI_SYNC_DRAFT',
  pending: 'APP_UI_SYNC_PENDING',
  landed: 'APP_UI_SYNC_LANDED',
  refused: 'APP_UI_SYNC_REFUSED',
  unknown: 'APP_UI_SYNC_UNKNOWN',
};

/** The server holds this record and the rep changed it since: Save still has something to send. */
export function hasEditsSinceSave(record: AppUiRecordDoc): boolean {
  return (
    !!record.serverId &&
    (record.syncStatus === 'draft' ||
      (record.syncStatus === 'landed' &&
        !!record.syncedOn &&
        record.modifiedOn > record.syncedOn))
  );
}

/** The i18n key for a record's sync status. */
export function syncStatusKey(record: AppUiRecordDoc): string {
  return hasEditsSinceSave(record)
    ? 'APP_UI_SYNC_UNSAVED'
    : (SYNC_STATUS_KEYS[record.syncStatus] ?? 'APP_UI_SYNC_DRAFT');
}
